import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { ElementBaseComponent } from '../ElementBaseComponent';
import { DataSet, ElementSettings, RowValue } from '../../../models/report-view.models';
import { HelpTextService } from '../../../services/services-index';

type PacingStatus = 'success' | 'warning' | 'danger';

interface KpiPacingMetricItemConfig {
  id?: string;
  label: string;
  actualMetric: string;
  targetMetric: string;
  progressRatioMetric: string;
  paceDeltaMetric: string;
  dailyRunRateMetric: string;
  progressText?: string;
  paceAheadText?: string;
  paceBehindText?: string;
  todayText?: string;
}

interface KpiPacingMetricItemViewModel {
  id: string;
  label: string;
  actualDisplay: string;
  targetDisplay: string;
  progressDisplay: string;
  paceDisplay: string;
  dailyRunRateDisplay: string;
  progressFillPercent: number;
  benchmarkTickPercent: number;
  status: PacingStatus;
  statusLabel: string;
  color: string;
  pillBackgroundColor: string;
  progressText: string;
  paceSupportingText: string;
  todayText: string;
}

@Component({
  selector: 'kpi-pacing-metrics',
  templateUrl: './kpi-pacing-metrics.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class KpiPacingMetricsComponent extends ElementBaseComponent {
  @Input() settings: ElementSettings;

  @Input() set dataSets(value: DataSet[] | null) {
    if (value?.length) {
      this.processData(value[0]);
      this.isDataSetLoaded$.next(true);
      return;
    }

    this.items = [];
    this.isDataSetLoaded$.next(false);
  }

  items: KpiPacingMetricItemViewModel[] = [];
  activeItemId: string | null = null;

  private paceBenchmarkRatio = 0;

  constructor(helpTextService: HelpTextService) {
    super(helpTextService);
  }

  setActiveItem(itemId: string | null): void {
    this.activeItemId = itemId;
  }

  getActiveItem(): KpiPacingMetricItemViewModel | undefined {
    return this.items.find(item => item.id === this.activeItemId);
  }

  getToneClass(status: PacingStatus): string {
    return `tone-${status}`;
  }

  private processData(dataSet: DataSet): void {
    if (!dataSet?.rows?.length) {
      throw new Error('KPI pacing metrics requires a populated dataset.');
    }

    const row = dataSet.rows[0];
    const options = this.getOptions();
    const itemConfigs = this.getItemConfigs(options);
    this.paceBenchmarkRatio = this.getMetricNumber(dataSet, row, options['paceBenchmarkRatioMetric'] ?? 'PaceBenchmarkRatio');

    this.items = itemConfigs.map((itemConfig, index) =>
      this.buildItemViewModel(dataSet, row, itemConfig, index));
  }

  private buildItemViewModel(
    dataSet: DataSet,
    row: RowValue[],
    itemConfig: KpiPacingMetricItemConfig,
    index: number
  ): KpiPacingMetricItemViewModel {
    const actualCell = this.getMetricCell(dataSet, row, itemConfig.actualMetric);
    const targetCell = this.getMetricCell(dataSet, row, itemConfig.targetMetric);
    const progressRatio = this.getMetricNumber(dataSet, row, itemConfig.progressRatioMetric);
    const paceDelta = this.getMetricNumber(dataSet, row, itemConfig.paceDeltaMetric);
    const dailyRunRateCell = this.getMetricCell(dataSet, row, itemConfig.dailyRunRateMetric);
    const status = this.getStatus(paceDelta);

    return {
      id: itemConfig.id ?? `pacing-metric-${index}`,
      label: itemConfig.label,
      actualDisplay: this.getDisplayValue(actualCell),
      targetDisplay: this.getDisplayValue(targetCell),
      progressDisplay: this.getPercentageDisplay(progressRatio),
      paceDisplay: this.getSignedPercentageDisplay(paceDelta),
      dailyRunRateDisplay: this.getDisplayValue(dailyRunRateCell),
      progressFillPercent: this.clamp(progressRatio * 100, 0, 100),
      benchmarkTickPercent: this.clamp(this.paceBenchmarkRatio * 100, 0, 100),
      status,
      statusLabel: this.getStatusLabel(status),
      color: this.getStatusColor(status),
      pillBackgroundColor: this.getStatusPillColor(status),
      progressText: itemConfig.progressText ?? 'of target achieved',
      paceSupportingText: paceDelta >= 0
        ? (itemConfig.paceAheadText ?? 'over ideal month-to-date pace')
        : (itemConfig.paceBehindText ?? 'behind ideal month-to-date pace'),
      todayText: itemConfig.todayText ?? 'per elapsed day'
    };
  }

  private getItemConfigs(options: Record<string, string>): KpiPacingMetricItemConfig[] {
    const rawItems = options['items'];

    if (!rawItems) {
      throw new Error('KPI pacing metrics requires settings.options.items.');
    }

    const parsedItems = JSON.parse(rawItems) as KpiPacingMetricItemConfig[];

    if (!Array.isArray(parsedItems) || parsedItems.length === 0) {
      throw new Error('KPI pacing metrics requires settings.options.items to be a non-empty array.');
    }

    parsedItems.forEach((item, index) => {
      const missingFields = [
        'label',
        'actualMetric',
        'targetMetric',
        'progressRatioMetric',
        'paceDeltaMetric',
        'dailyRunRateMetric'
      ].filter(field => !item[field as keyof KpiPacingMetricItemConfig]);

      if (missingFields.length) {
        throw new Error(`KPI pacing metrics item at index ${index} is missing: ${missingFields.join(', ')}.`);
      }
    });

    return parsedItems;
  }

  private getMetricCell(dataSet: DataSet, row: RowValue[], metricName: string): RowValue {
    const columnIndex = dataSet.columns.findIndex(column => column.name === metricName);

    if (columnIndex < 0) {
      throw new Error(`KPI pacing metrics column '${metricName}' was not found in dataset '${dataSet.name}'.`);
    }

    return row[columnIndex];
  }

  private getMetricNumber(dataSet: DataSet, row: RowValue[], metricName: string): number {
    const cell = this.getMetricCell(dataSet, row, metricName);
    const numericValue = this.toNumber(cell.value);

    if (numericValue === null) {
      throw new Error(`KPI pacing metrics column '${metricName}' must contain a numeric value.`);
    }

    return numericValue;
  }

  private getStatus(paceDelta: number): PacingStatus {
    const warningThreshold = this.getOptionNumber('warningThreshold', -0.1);
    const successThreshold = this.getOptionNumber('successThreshold', 0);

    if (paceDelta >= successThreshold) {
      return 'success';
    }

    if (paceDelta >= warningThreshold) {
      return 'warning';
    }

    return 'danger';
  }

  private getStatusLabel(status: PacingStatus): string {
    const options = this.getOptions();

    if (status === 'success') {
      return options['successLabel'] ?? 'On Pace';
    }

    if (status === 'warning') {
      return options['warningLabel'] ?? 'Slightly behind';
    }

    return options['dangerLabel'] ?? 'Well behind';
  }

  private getStatusColor(status: PacingStatus): string {
    const options = this.getOptions();

    if (status === 'success') {
      return options['successColor'] ?? '#21a85b';
    }

    if (status === 'warning') {
      return options['warningColor'] ?? '#f28c3c';
    }

    return options['dangerColor'] ?? '#f1535b';
  }

  private getStatusPillColor(status: PacingStatus): string {
    const options = this.getOptions();

    if (status === 'success') {
      return options['successPillColor'] ?? '#e1f6ea';
    }

    if (status === 'warning') {
      return options['warningPillColor'] ?? '#fff0e3';
    }

    return options['dangerPillColor'] ?? '#ffe4e7';
  }

  private getDisplayValue(cell: RowValue | undefined): string {
    if (!cell) {
      return 'N/A';
    }

    if (cell.label !== undefined && cell.label !== null && cell.label !== '') {
      return `${cell.label}`;
    }

    if (cell.value === undefined || cell.value === null || cell.value === '') {
      return 'N/A';
    }

    if (typeof cell.value === 'number') {
      return cell.value.toLocaleString('en-US', {
        maximumFractionDigits: Number.isInteger(cell.value) ? 0 : 1
      });
    }

    return `${cell.value}`;
  }

  private getPercentageDisplay(value: number): string {
    const precision = this.getOptionNumber('progressPrecision', 0);
    return `${(value * 100).toFixed(precision)}%`;
  }

  private getSignedPercentageDisplay(value: number): string {
    const precision = this.getOptionNumber('pacePrecision', 0);
    const sign = value >= 0 ? '+' : '-';
    return `${sign}${Math.abs(value * 100).toFixed(precision)}%`;
  }

  private getOptionNumber(name: string, fallback: number): number {
    const value = Number(this.getOptions()[name]);
    return Number.isFinite(value) ? value : fallback;
  }

  private getOptions(): Record<string, string> {
    const rawOptions = this.settings?.options as unknown;

    if (!rawOptions) {
      return {};
    }

    if (Array.isArray(rawOptions)) {
      return rawOptions.reduce((optionsMap: Record<string, string>, entry: unknown) => {
        if (Array.isArray(entry) && entry.length > 1) {
          optionsMap[`${entry[0]}`] = `${entry[1]}`;
        }

        return optionsMap;
      }, {});
    }

    return rawOptions as Record<string, string>;
  }

  private toNumber(value: unknown): number | null {
    if (typeof value === 'number' && Number.isFinite(value)) {
      return value;
    }

    if (typeof value === 'string') {
      const parsed = Number(value.replace(/,/g, '').trim());
      return Number.isFinite(parsed) ? parsed : null;
    }

    return null;
  }

  private clamp(value: number, min: number, max: number): number {
    return Math.min(max, Math.max(min, value));
  }
}
